const BODY_ALLOWED_TAGS = new Set([
  "p",
  "h1",
  "h2",
  "h3",
  "strong",
  "em",
  "u",
  "s",
  "ul",
  "ol",
  "li",
  "blockquote",
  "hr",
  "br",
]);

const BODY_DROP_CONTENT_TAGS = new Set([
  "script",
  "style",
  "iframe",
  "object",
  "embed",
  "noscript",
]);

const BODY_INLINE_TAGS = new Set(["strong", "em", "u", "s"]);
const BODY_BLOCK_TAGS = new Set(["p", "h1", "h2", "h3", "blockquote", "li"]);

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function normalizeLineEndings(value: string): string {
  return value.replace(/\r\n?/g, "\n");
}

function hasMeaningfulSerializedContent(value: string): boolean {
  const withoutTags = value
    .replace(/<br>/gi, "")
    .replace(/<hr>/gi, "")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .trim();

  return withoutTags.length > 0;
}

function serializeAllowedNode(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return escapeHtml(node.textContent || "");
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return "";
  }

  const element = node as HTMLElement;
  const tagName = element.tagName.toLowerCase();

  if (BODY_DROP_CONTENT_TAGS.has(tagName)) {
    return "";
  }

  if (tagName === "br") {
    return "<br>";
  }

  if (tagName === "hr") {
    return "<hr>";
  }

  const childHtml = Array.from(element.childNodes)
    .map((childNode) => serializeAllowedNode(childNode))
    .join("");

  if (!BODY_ALLOWED_TAGS.has(tagName)) {
    return childHtml;
  }

  if (BODY_INLINE_TAGS.has(tagName) && !hasMeaningfulSerializedContent(childHtml)) {
    return "";
  }

  if (BODY_BLOCK_TAGS.has(tagName) && !hasMeaningfulSerializedContent(childHtml)) {
    return "";
  }

  if ((tagName === "ul" || tagName === "ol") && !childHtml.trim()) {
    return "";
  }

  return `<${tagName}>${childHtml}</${tagName}>`;
}

function serializeAllowedHtml(value: string): string {
  if (typeof document === "undefined") {
    return value.trim();
  }

  const container = document.createElement("div");
  container.innerHTML = value;

  return Array.from(container.childNodes)
    .map((node) => serializeAllowedNode(node))
    .join("")
    .replace(/\r\n?/g, "\n")
    .replace(/>\s+</g, "><")
    .trim();
}

export function bodyHasHtml(value: string | null | undefined): boolean {
  return /<[a-z][\s\S]*>/i.test(String(value ?? ""));
}

export function normalizeMessageBodyText(value: string | null | undefined): string {
  const normalized = normalizeLineEndings(String(value ?? ""));
  const paragraphs = normalized
    .split(/\n{2,}/)
    .map((paragraph) =>
      paragraph
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line.length > 0)
        .join("\n")
        .trim(),
    )
    .filter((paragraph) => paragraph.length > 0);

  return paragraphs.join("\n\n");
}

export function plainTextToMessageBodyHtml(
  value: string | null | undefined,
): string {
  const normalized = normalizeMessageBodyText(value);
  if (!normalized) {
    return "";
  }

  return normalized
    .split("\n\n")
    .map((paragraph) => {
      const htmlParagraph = paragraph
        .split("\n")
        .map((line) => escapeHtml(line))
        .join("<br>");
      return `<p>${htmlParagraph}</p>`;
    })
    .join("");
}

export function extractMessageBodyPlainTextForEditor(
  value: string | null | undefined,
): string {
  const rawValue = String(value ?? "");
  if (!rawValue.trim()) {
    return "";
  }

  if (!bodyHasHtml(rawValue)) {
    return normalizeMessageBodyText(rawValue);
  }

  if (typeof document === "undefined") {
    return normalizeMessageBodyText(rawValue.replace(/<[^>]+>/g, " "));
  }

  const textHintedHtml = rawValue
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<hr\s*\/?>/gi, "\n\n")
    .replace(/<\/(p|div|blockquote|h[1-6]|ul|ol)>/gi, "</$1>\n\n")
    .replace(/<\/li>/gi, "</li>\n");

  const container = document.createElement("div");
  container.innerHTML = textHintedHtml;
  return normalizeMessageBodyText(container.textContent || "");
}

export function extractMessageBodyPlainText(
  value: string | null | undefined,
): string {
  return extractMessageBodyPlainTextForEditor(value);
}

export function canonicalizeMessageBodyHtml(
  value: string | null | undefined,
): string {
  const rawValue = String(value ?? "").trim();
  if (!rawValue) {
    return "";
  }

  const normalizedHtml = bodyHasHtml(rawValue)
    ? serializeAllowedHtml(rawValue)
    : plainTextToMessageBodyHtml(rawValue);

  if (!extractMessageBodyPlainText(normalizedHtml).trim()) {
    return "";
  }

  return normalizedHtml;
}

export function flattenMessageBodyToPlainTextHtml(
  value: string | null | undefined,
): string {
  return plainTextToMessageBodyHtml(extractMessageBodyPlainTextForEditor(value));
}

export function normalizeMessageBodyForRender(
  value: string | null | undefined,
): string {
  return canonicalizeMessageBodyHtml(value);
}

export function buildMessageBodySnippet(
  value: string | null | undefined,
): string {
  const plainText = extractMessageBodyPlainText(value)
    .replace(/\s+/g, " ")
    .trim();

  const words = plainText ? plainText.split(" ") : [];
  const snippetWords = words.slice(0, 15);

  if (snippetWords.length === 0) {
    return "";
  }

  return snippetWords.join(" ") + (words.length > 30 ? "..." : "");
}
